/**
 * Hata normalizasyonu.
 *
 * API tüm hataları RFC 7807 `application/problem+json` olarak döner. Burada
 * tek bir hata sınıfına çevrilir; çağıran taraf `problem.type` ile ne olduğunu
 * makine okunur şekilde ayırt eder — HTTP durum koduna bakıp tahmin etmez.
 */

/** RFC 7807 gövdesi. `type` `urn:akillidernek:error:<kod>` biçimindedir. */
export type ProblemDetail = {
  type: string
  title: string
  status: number
  detail: string
  instance?: string
  request_id?: string
  /** Doğrulama hatalarında alan bazlı liste. */
  errors?: Array<{ field: string; message: string; type: string }>
  /** Özellik kapısında hangi özelliğin eksik olduğu. */
  feature?: string
  /** Kota aşımında sınır bilgisi. */
  limit?: string
  allowed?: number
  used?: number
}

export const ERROR_PREFIX = 'urn:akillidernek:error:'

/** Bilinen hata kodları — API'deki `AppError` alt sınıflarıyla birebir. */
export const ErrorCode = {
  NotFound: 'not_found',
  Conflict: 'conflict',
  Validation: 'validation_error',
  Unauthenticated: 'unauthenticated',
  PermissionDenied: 'permission_denied',
  FeatureNotAvailable: 'feature_not_available',
  QuotaExceeded: 'quota_exceeded',
  TenantNotResolved: 'tenant_not_resolved',
  TenantInactive: 'tenant_inactive',
} as const

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode]

export class ApiError extends Error {
  readonly problem: ProblemDetail
  readonly status: number

  constructor(problem: ProblemDetail) {
    super(problem.detail || problem.title)
    this.name = 'ApiError'
    this.problem = problem
    this.status = problem.status
  }

  /** `urn:` önekinden arındırılmış hata kodu. */
  get code(): string {
    return this.problem.type.startsWith(ERROR_PREFIX)
      ? this.problem.type.slice(ERROR_PREFIX.length)
      : this.problem.type
  }

  is(code: ErrorCodeValue): boolean {
    return this.code === code
  }

  /** Doğrulama hatalarını `{ alan: mesaj }` sözlüğüne çevirir (form gösterimi). */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(
      (this.problem.errors ?? []).map((error) => [error.field, error.message]),
    )
  }
}

/** Yanıt gövdesi problem+json değilse bile kullanılabilir bir hata üretir. */
export function toApiError(status: number, body: unknown): ApiError {
  if (body && typeof body === 'object' && 'type' in body && 'title' in body) {
    return new ApiError(body as ProblemDetail)
  }
  return new ApiError({
    type: `${ERROR_PREFIX}unknown`,
    title: 'Beklenmeyen bir hata oluştu',
    status,
    detail: typeof body === 'string' && body ? body : `HTTP ${status}`,
  })
}
